import React, { useState, useEffect } from 'react';
import { FaList, FaSitemap } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import {
  GetReferralData,
  GetReferralTreeData,
  GetReferralUserChildren,
} from '../../features/reportingSlice';
import { ReferralTreeNode } from '../../utils/type';
import Table from '../../components/table/Table';
import TreeTableDemo from '../../components/table/TreeTable';
import Button from '../../components/button/Button';
import useExportCSV from '../../hooks/useExportCSV';
import { getCookieValue } from '../../App';
import axios from 'axios';
import HistoryRouting from '../../components/HistoryRouting';

export const referrerTableConfig = {
  headers: [
    { key: 'name', label: 'Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'confirmed', label: 'Confirmed' },
    { key: 'pending', label: 'Pending' },
    { key: 'expired', label: 'Expired' },
    { key: 'total', label: 'Total' },
  ],
};

type ViewType = 'list' | 'tree';

const MemberReferral = () => {
  const [currentView, setCurrentView] = useState<ViewType>('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | ''>('');
  const [filterBy, setFilterBy] = useState<
    | 'top10'
    | 'top100'
    | 'alltime'
    | 'currentmonth'
    | 'last30days'
    | 'lastmonth'
    | ''
  >('');

  const resetFilter = () => {
    setFilterBy('');
    setSortBy('');
    setSortDirection('');
    setCurrentPage(1);
  };

  // Dynamic limit based on filterBy
  const limit = filterBy === 'top100' ? 100 : 10;

  const dispatch = useDispatch();
  const { referralData, referralTreeData, loadingStates } = useSelector(
    (state: RootState) => state.reporting
  );
  const handleExportCSV = useExportCSV();

  const handleLoadChildren = async (
    referrerUserId: number
  ): Promise<ReferralTreeNode[]> => {
    try {
      const result = await dispatch(
        GetReferralUserChildren({ userId: referrerUserId }) as any
      );

      return result || [];
    } catch (error) {
      console.error('Error loading children:', error);
      return [];
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSortChange = (newSortBy: string) => {
    console.log('newSortBy: ', newSortBy);
    setSortBy(newSortBy);
    setCurrentPage(1);
  };

  const handleSortDirectionChange = (newSortDirection: 'asc' | 'desc' | '') => {
    setSortDirection(newSortDirection);
    setCurrentPage(1);
  };

  // Export function for List view
  const exportListData = async (isExporting: boolean = false) => {
    if (isExporting) {
      try {
        const token = getCookieValue('token');
        const requestPayload: any = {
          limit: 1000, // Get more data for export
          offset: 0,
          exportToCSV: true,
        };

        if (sortBy) requestPayload.sortBy = sortBy;
        if (sortDirection) requestPayload.order = sortDirection;
        if (filterBy) requestPayload.filterBy = filterBy;

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/r/d`,
          requestPayload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200 && response.data.data) {
          handleExportCSV(
            referrerTableConfig,
            response.data.data,
            'List of Referrers'
          );
        }
      } catch (error) {
        console.error('Error exporting list data:', error);
      }
    }
  };

  // Export function for Tree view - same pattern as List
  const exportTreeData = async (isExporting: boolean = false) => {
    if (isExporting) {
      try {
        const token = getCookieValue('token');
        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/r/t`,
          {
            exportToCSV: true,
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200 && response.data) {
          // Flatten the tree data for CSV export
          const flattenTreeData = (nodes: any[], level = 0): any[] => {
            const result: any[] = [];
            nodes.forEach(node => {
              const directStats = node.directReferrals || {
                confirmed: node.confirmed || 0,
                pending: node.pending || 0,
                expired: node.expired || 0,
              };
              const orgStats = node.organizationReferrals || {
                confirmed: node.confirmed || 0,
                pending: node.pending || 0,
                expired: node.expired || 0,
              };

              result.push({
                name: '  '.repeat(level) + (node.name || node.email),
                phone: node.phone,
                confirmed_direct: directStats.confirmed,
                pending_direct: directStats.pending,
                expired_direct: directStats.expired,
                total_direct:
                  directStats.total ||
                  directStats.confirmed +
                    directStats.pending +
                    directStats.expired,
                confirmed_organization: orgStats.confirmed,
                pending_organization: orgStats.pending,
                expired_organization: orgStats.expired,
                total_organization:
                  orgStats.total ||
                  orgStats.confirmed + orgStats.pending + orgStats.expired,
              });

              if (node.children && node.children.length > 0) {
                result.push(...flattenTreeData(node.children, level + 1));
              }
            });
            return result;
          };

          const treeData = Array.isArray(response.data)
            ? response.data
            : response.data.data || [];
          const flatData = flattenTreeData(treeData);

          const treeTableConfig = {
            headers: [
              { key: 'name', label: 'Name' },
              { key: 'phone', label: 'Phone' },
              { key: 'confirmed_direct', label: 'Direct Confirmed' },
              { key: 'pending_direct', label: 'Direct Pending' },
              { key: 'expired_direct', label: 'Direct Expired' },
              { key: 'total_direct', label: 'Direct Total' },
              {
                key: 'confirmed_organization',
                label: 'Organization Confirmed',
              },
              { key: 'pending_organization', label: 'Organization Pending' },
              { key: 'expired_organization', label: 'Organization Expired' },
              { key: 'total_organization', label: 'Organization Total' },
            ],
          };

          handleExportCSV(treeTableConfig, flatData, 'Referrals Tree');
        }
      } catch (error) {
        console.error('Error exporting tree data:', error);
      }
    }
  };

  // Reset to first page when filter, view, or limit changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterBy, currentView, limit, sortDirection]);

  // Fetch data on component mount and when parameters change
  useEffect(() => {
    const offset = (currentPage - 1) * limit;
    const referralParams: any = {
      limit,
      offset,
    };

    if (sortBy) referralParams.sortBy = sortBy;
    if (sortDirection) referralParams.order = sortDirection;
    if (filterBy) referralParams.filterBy = filterBy;
    if (currentView === 'tree') {
      dispatch(GetReferralTreeData() as any);
    } else if (currentView === 'list') {
      dispatch(GetReferralData(referralParams) as any);
    }
  }, [
    dispatch,
    limit,
    currentPage,
    sortBy,
    sortDirection,
    filterBy,
    currentView,
  ]);

  return (
    <div className='space-y-6'>
      {/* Header */}
      <HistoryRouting history='Reporting/Member Referrals' />

      {/* View Toggle Buttons - Customer Data Style */}
      <div className='border border-gray-200 shadow px-7 pt-7 pb-5'>
        <div className='flex gap-10'>
          <Button
            className={`${
              currentView === 'list'
                ? 'bg-[#00C389] text-white'
                : 'text-black bg-white'
            } w-full`}
            onClick={() => {
              setCurrentView('list');
              resetFilter();
            }}
          >
            <FaList className='w-4 h-4 mr-2' />
            List View
          </Button>
          <Button
            className={`${
              currentView === 'tree'
                ? 'bg-[#00C389] text-white'
                : 'text-black bg-white'
            } w-full`}
            onClick={() => {
              setCurrentView('tree');
              resetFilter();
            }}
          >
            <FaSitemap className='w-4 h-4 mr-2' />
            Tree View
          </Button>
        </div>
      </div>

      {/* Filter and Sort Controls */}
      {currentView !== 'tree' && (
        <div className='flex justify-between  bg-white p-4 rounded-lg shadow-sm'>
          <div className='flex items-center gap-2'>
            <label
              htmlFor='filterBy'
              className='text-sm font-medium text-gray-700'
            >
              Filter by:
            </label>

            <select
              id='filterBy'
              value={filterBy}
              onChange={e => setFilterBy(e.target.value as any)}
              className='border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-36'
            >
              <option value=''>Select Filter</option>
              <option value='alltime'>All Time</option>
              <option value='top10'>Top 10</option>
              <option value='top100'>Top 100</option>
              <option value='currentmonth'>Current Month</option>
              <option value='last30days'>Last 30 Days</option>
              <option value='lastmonth'>Last Month</option>
            </select>
          </div>
          <div className='flex items-center gap-4'>
            <div className='flex items-center gap-2'>
              <label
                htmlFor='sortBy'
                className='text-sm font-medium text-gray-700'
              >
                Sort by:
              </label>

              <select
                id='sortBy'
                value={sortBy}
                onChange={e => setSortBy(e.target.value as any)}
                className='border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-36'
              >
                <option value=''>Select Sort</option>
                <option value='total'>Total</option>
                <option value='confirmed'>Confirmed</option>
                <option value='pending'>Pending</option>
                <option value='expired'>Expired</option>
                <option value='name'>Name</option>
              </select>
            </div>

            <div className='flex items-center gap-2'>
              <label
                htmlFor='sortDirection'
                className='text-sm font-medium text-gray-700'
              >
                Sort direction:
              </label>

              <select
                id='sortDirection'
                value={sortDirection}
                onChange={e =>
                  handleSortDirectionChange(
                    e.target.value as 'asc' | 'desc' | ''
                  )
                }
                className='border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-36'
              >
                <option value=''>Select Direction</option>
                <option value='asc'>Ascending</option>
                <option value='desc'>Descending</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Conditional Rendering based on selected view */}
      {currentView === 'tree' ? (
        <>
          {console.log('MemberReferral referralTreeData:', referralTreeData)}
          <TreeTableDemo
            data={referralTreeData ?? []}
            title='Referrals Tree'
            onLoadChildren={handleLoadChildren}
            showExportButton={true}
            customCSVExporting={exportTreeData}
            // Server-side pagination and sorting
            serverSideMode={true}
            totalCount={referralTreeData?.length}
            currentPage={currentPage}
            totalPages={1}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            loading={loadingStates['GetReferralTreeData'] ?? false}
            rowsPerPage={limit}
          />
        </>
      ) : (
        <Table
          data={referralData?.data ?? []}
          config={referrerTableConfig}
          title='List of Referrers'
          width='w-full'
          showExportButton={true}
          rowsPerPage={limit}
          enableSorting={false}
          // Server-side pagination and sorting
          serverSideMode={true}
          totalCount={referralData?.total ?? 0}
          currentPage={currentPage}
          totalPages={Math.ceil((referralData?.total ?? 0) / limit)}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
          showSearch={false}
          // Custom CSV export function
          customCSVExporting={exportListData}
        />
      )}
    </div>
  );
};

export default MemberReferral;
